/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useState, useEffect } from 'react'
import Image from 'next/image'
import { createPortal } from 'react-dom'
import { allergensList, getAllergenName } from '@/lib/utils/allergensPath'
import { Currency, ITheme, Language } from '@/lib/interfaces/interfaces'
import { NextFont } from 'next/dist/compiled/@next/font'
import OrderingWidget from './ordering/OrderingWidget'
import { formatMoney } from '@/lib/utils/format'

interface DetailedPopUpProps {
	setShowFullItem: (show: boolean) => void
	name: string
	description?: string
	detailText?: string
	imageSource?: string | null
	allergens?: string[]
	price?: number | string
	halfprice?: number | string
	tapaprice?: number | string
	fourthprice?: number | string
	priceLabel?: string
	halfpriceLabel?: string
	tapapriceLabel?: string
	allergenImages: string[] | undefined
	isFood?: boolean
	theme: ITheme
  language: Language
	fontStyle: {
		fontFamily: NextFont
		fontSize: string
	},
  currency: Currency
  orderingActive: boolean
  priceMarkup?: number
  itemId: string
}

const DetailedPopUp = ({
  setShowFullItem,
  name,
  description,
  detailText,
  imageSource,
  allergens,
  price,
  halfprice,
  tapaprice,
  priceLabel,
  halfpriceLabel,
  tapapriceLabel,
  isFood,
  theme,
  fontStyle,
  currency,
  language,
  orderingActive,
  priceMarkup = 0,
  itemId
}: DetailedPopUpProps) => {
  const [isVisible, setIsVisible] = useState(true)

  const priceStyle = { backgroundColor: theme.items.bgPriceColor, color: theme.items.priceTextColor }

  const toNumber = (value: number | string | undefined) => {
    if (value === undefined) return undefined
    const parsed = typeof value === 'string' ? parseFloat(value) : value
    return Number.isFinite(parsed) ? parsed : undefined
  }

  const hasValidPrice = (value: number | string | undefined) => {
    const parsed = toNumber(value)
    return parsed !== undefined && parsed !== 0
  }

  const hasAnyPrice = hasValidPrice(tapaprice) || hasValidPrice(halfprice) || hasValidPrice(price)
  const hasAllergens = allergens != null && allergens.length > 0

  const priceTextHex = theme.items.priceTextColor.replace('#', '')
  const priceTextR = parseInt(priceTextHex.slice(0, 2), 16)
  const priceTextG = parseInt(priceTextHex.slice(2, 4), 16)
  const priceTextB = parseInt(priceTextHex.slice(4, 6), 16)
  const isDarkTheme = (priceTextR * 0.299 + priceTextG * 0.587 + priceTextB * 0.114) > 150
  const separatorColor = isDarkTheme ? '#e2e2e2' : theme.items.dividerColor
  const separatorStyle = { background: `linear-gradient(90deg, transparent, ${separatorColor}, transparent)` }

  const renderPriceRow = (value: number | string | undefined, label?: string) => {
    if (!hasValidPrice(value)) return null

    const showLabel = Boolean(label && label.trim().length > 0)
    return (
      <div className='price-row'>
        {showLabel && <span className='price-row__label'>{label}</span>}
        {showLabel && <span className='price-row__dots' />}
        <span className={`price-row__value ${fontStyle.fontFamily.className}`} style={priceStyle}>
          {formatPrice(value!)}
        </span>
      </div>
    )
  }

  const formatPrice = (rawPrice: number | string) => {
    const priceNumber = toNumber(rawPrice)

    if (priceNumber === undefined) return ''

    // En delivery el recargo es un importe fijo por producto/variante.
    const finalPrice = orderingActive && priceMarkup > 0
      ? priceNumber + priceMarkup
      : priceNumber

    return formatMoney(finalPrice, currency)
  }

  const handleClose = () => {
    setIsVisible(false)
  }

  // Función para cerrar el popup con animación, se pasa al OrderingWidget
  const closePopup = () => {
    setIsVisible(false)
  }

  useEffect(() => {
    if (!isVisible) {
      // Espera a que la animación termine antes de cerrar
      const timer = setTimeout(() => {
        setShowFullItem(false)
      }, 300) // Debe coincidir con la duración de la animación

      return () => clearTimeout(timer)
    }
  }, [isVisible, setShowFullItem])

  return createPortal(
    <div className={`overlay ${isVisible ? 'fade-in' : 'fade-out'} ${orderingActive ? 'overlay--with-ordering' : ''}`} onClick={handleClose}>
      <div className={`detailed-item ${isVisible ? 'scale-up' : 'scale-down'} ${orderingActive ? 'detailed-item--with-ordering' : ''}`} onClick={(e) => e.stopPropagation()}>
        {/* <X
          className='close-icon'
          style={{ backgroundColor: theme.items.bgPriceColor }}
          onClick={handleClose}
        /> */}

        {imageSource && (
          <Image src={imageSource} className={!orderingActive ? 'product-img' : 'product-img thin-image'} alt={`imagen de ${name}`} width={400} height={300} style={{ height: 'auto' }} />
        )}

        <div className='detailed-item__info'>
          <div className='detailed-item__header'>
            <h5 className={fontStyle.fontFamily.className} style={{ color: theme.items.nameColor }}>{name}</h5>
            {description && <p className={`detailed-item__description ${fontStyle.fontFamily.className}`} style={{ color: theme.items.descriptionColor }}>{description}</p>}
            {detailText && <p className='detailed-text'>{detailText}</p>}
          </div>

          {hasAnyPrice && (
            <>
              <div className='detailed-item__separator' style={separatorStyle} />
              <div className='detailed-item__prices'>
                {renderPriceRow(tapaprice, tapapriceLabel)}
                {renderPriceRow(halfprice, halfpriceLabel)}
                {renderPriceRow(price, priceLabel)}
              </div>
            </>
          )}

          {hasAllergens && (
            <>
              <div className='detailed-item__separator' style={separatorStyle} />
              <div className='detailed-item__allergens'>
                <div className='allergens-container center'>
                  {allergens.map((allergen) => (
                    <div
                      key={allergen}
                      data-allergen={getAllergenName(allergen, language)}
                      className='allergen__img-wrapper'
                    >
                      <Image
                        className='allergen__img'
                        width={40}
                        height={40}
                        src={allergensList[allergen].img}
                        alt={allergen}
                        style={{ height: 'auto' }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
        {orderingActive && (
          <>
            <div className='detailed-item__separator detailed-item__separator--ordering' style={separatorStyle} />
            <OrderingWidget
              isVisible={isVisible}
              itemId={itemId}
              itemName={name}
              price={price}
              halfprice={halfprice}
              tapaprice={tapaprice}
              priceLabel={priceLabel}
              halfpriceLabel={halfpriceLabel}
              tapapriceLabel={tapapriceLabel}
              priceMarkup={priceMarkup}
              closePopup={closePopup}
              language={language}
              currency={currency}
            />
          </>
        )}
      </div>
    </div>,
    document.body
  )
}

export default DetailedPopUp
